import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { concatMap, map, Observable } from 'rxjs';
import { StorageService } from 'core-tsi';
import { ArchivoBase64 } from '../models/adjuntos/archivo-base-64';
import { ClaseCabecera, Recursos, TBanco, TipoDocumento } from 'ng-components-tsi';
import { TipoCambioResponse } from '../../modulos/archivos/tipo-cambio/models/tipo-cambio-response.model';

import { DatePipe } from '@angular/common';
import { InventarioSesion } from 'core-tsi';
import { AlmacenSesion } from 'core-tsi';
import { UnidadesSesion } from 'core-tsi';
import { TipoCambioService } from '../../modulos/archivos/tipo-cambio/data-access/tipo-cambio.service';
import { TipoCambioImportSunat } from '../../modulos/archivos/tipo-cambio/models/tipo-cambio-import-sunat.model';
import { ToastService } from 'ng-components-tsi';
import { TituloListas } from 'core-tsi';
import { DocumentosTipo } from 'core-tsi';
import { urlErp } from '../utils/general/url';
import { esFechaMayorActual } from 'shared-tsi';
import { RecursosArticulo } from '../../modulos/activos-fijos/activos/models/articulo-response.model';

const urlTBancos = `${urlErp}/tbancos`

@Injectable({
  providedIn: 'root'
})
export class RecursosService {

  private http = inject(HttpClient)
  private datePipe = inject(DatePipe)
  private tipoCambioService = inject(TipoCambioService)
  private toast = inject(ToastService)

  findSituacionByTipDoc(tipdoc: number, situaciones?: string): Observable<TipoDocumento[]> {
    let params = new HttpParams()
      .set('tipdoc', tipdoc)
    if (situaciones) {
      params = params.set('situaciones', situaciones)
    }
    return this.http.get<any>(`${urlErp}/situacion/buscar`, { params }).pipe(
      map(res => res.list || [])
    )
  }

  findAllRecursosByTipoDoc(tipdoc: number, moting: boolean, codmod: number): Observable<Recursos> {
    const params = new HttpParams()
      .set('codsuc', this.getSucursalSelected() ?? this.getSession().codsuc)
      .set('moting', moting)
      .set('codmod', codmod ?? '')
    return this.http.get<Recursos>(`${urlErp}/general/recursos/${tipdoc}`, { params })
  }

  findAllRecursosArticulo(): Observable<RecursosArticulo> {
    return this.http.get<RecursosArticulo>(`${urlErp}/general/articulo/recursos`)
  }

  findAllTarjetas(): Observable<any[]> {
    return this.http.get<any[]>(`${urlErp}/general/tarjetas`)
  }

  findAllClasesByTipoDoc(tipdoc: number, soloObligatorios?: boolean, soloEditables?: boolean): Observable<ClaseCabecera[]> {
    const params = new HttpParams()
      .set('soloObligatorios', soloObligatorios ? soloObligatorios : false)
      .set('soloEditables', soloEditables ? soloEditables : false)
    return this.http.get<ClaseCabecera[]>(`${urlErp}/documento-generico/${tipdoc}/clases-cabecera`, { params })
  }

  findTBancos(activo?: boolean, clase?: number): Observable<TBanco[]> {
    const params = new HttpParams()
      .set('activo', activo ?? true)
      .set('clase', clase ?? -1)
    return this.http.get<TBanco[]>(`${urlTBancos}/buscar`, { params })
  }

  getSession() {
    return StorageService.getSession()
  }

  getMonedas() {
    return this.getSession() ? (this.getSession().monedas ?? []).filter(mon => mon.codmon !== 'EUR') : []
  }

  getVendedores() {
    return this.getSession() ? (this.getSession().vendedores ?? []) : []
  }

  getVendedoresForService(desven: string) {
    const params = new HttpParams()
      .set('desven', desven)
    return this.http.get<any>(`${urlErp}/vendedor/buscar`, { params })
  }

  getMediosPago(activo: boolean) {
    const params = new HttpParams()
      .set('activo', activo)
    return this.http.get<any>(`${urlErp}/medios-pago/buscar`, { params })
  }

  getMotivos(codigo: number) {
    const motivos = this.getSession()?.motivos ?? [];
    return motivos.filter((motivo: any) => motivo.tipdoc == codigo)
  }

  getSituaciones(codigo: number) {
    const situaciones = this.getSession()?.situaciones ?? [];
    return situaciones.filter((situacion: any) => situacion.tipdoc == codigo)
  }

  getSucursales() {
    return this.getSession() ? (this.getSession().sucursales ?? []) : []
  }

  getlEmisiones() {
    return this.getSession() ? (this.getSession().lugarEmisions ?? []) : []
  }

  getTipoDocumentos() {
    return this.getSession() ? (this.getSession().documentoTipos ?? []) : []
  }

  getIdentidades() {
    return this.getSession() ? (this.getSession().identidades ?? []) : []
  }

  getCodUser() {
    return this.getSession() ? this.getSession().coduser : ''
  }

  getSucursalSelected() {
    return StorageService.getCodSuc()
  }

  getIdCia() {
    return this.getSession() ? this.getSession().id_cia : ''
  }

  getCia() {
    return this.getSession() ? this.getSession().cia : ''
  }

  getRuc() {
    return this.getSession() ? this.getSession().ruc : ''
  }

  getAccesos() {
    return this.getSession() ? this.getSession().accesos : []
  }

  getInventarios(): InventarioSesion[] {
    return this.getSession() ? (this.getSession().inventarios ?? []) : []
  }

  getAlmacenes(): AlmacenSesion[] {
    return this.getSession() ? (this.getSession().almacenes ?? []) : []
  }

  getUnidadesMedida(): UnidadesSesion[] {
    return this.getSession() ? (this.getSession().unidades ?? []) : []
  }

  getTituloListas(): TituloListas[] {
    return this.getSession() ? (this.getSession().tituloListas ?? []) : []
  }

  getDocumentosTipo(filtro?: number[]): DocumentosTipo[] {
    const documentos = this.getSession()?.documentoTipos ?? [];
    if (filtro) {
      return documentos.filter(doc => filtro.includes(doc.tipdoc));
    }
    return documentos;
  }

  getSunat() {
    return this.http.get<any>(`${urlErp}/estado/envioSunat/buscar`)
  }

  getFactores() {
    return this.getSession() ? this.getSession().factores : null
  }

  getGrilla(id: string): any[] {
    const grillas = StorageService.getGrilla()
    const encontrar = grillas.find(item => (item.idform ?? '') == id)
    if (encontrar) {
      const columns = encontrar.columns ?? []
      const orderColumn = columns.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      return orderColumn
    }
    return []
  }

  // FACTORES
  getFactorByKey(extraerFactor: string) {
    const factores = this.getFactores()
    if (factores) return factores[extraerFactor]
  }

  getTipoDocumentoForService(tipdocs: string, descri: string): Observable<any[]> {
    const params = new HttpParams()
      .set('tipdocs', tipdocs)
      .set('descri', descri)
    return this.http.get<any[]>(`${urlErp}/documentos-tipo/buscar`, { params })
  }

  getMotivosForService(tipdoc: number, id: string): Observable<any[]> {
    const params = new HttpParams()
      .set('tipdoc', tipdoc)
      .set('id', id)
    return this.http.get<any[]>(`${urlErp}/motivo/buscar`, { params })
  }

  getDocumentosCobranza(limit: number, offset: number): Observable<any[]> {
    const params = new HttpParams()
      .set('limit', limit)
      .set('offset', offset)
    return this.http.get<any[]>(`${urlErp}/documento-cobranza/consultar`, { params })
  }

  getCondicionesPagos() {
    return this.getSession() ? (this.getSession().cPagos ?? []) : []
  }

  getTipoDocumentoForServiceV2(codigos: any) {
    const params = new HttpParams()
      .set('codigos', codigos)
    return this.http.get<any[]>(`${urlErp}/tdocume/buscar`, { params })
  }

  getCuentaForProveedor(tipdoc: string, codprov: string, codmon: string) {
    const params = new HttpParams()
      .set('codprov', codprov)
      .set('codmon', codmon)
    return this.http.get<any[]>(`${urlErp}/tdocume/${tipdoc}/cuenta`, { params })
  }

  getUsuarios(coduser: string, nombres: string) {
    const params = new HttpParams()
      .set('coduser', coduser)
      .set('nombres', nombres)
    return this.http.get<any[]>(`${urlErp}/usuario/buscar`, { params })
  }

  getProyectos(descri: string, limit: number, offset: number) {
    const params = new HttpParams()
      .set('descri', descri)
      .set('limit', limit)
      .set('offset', offset)
    return this.http.get<any[]>(`${urlErp}/plan-cuentas/tproyecto/buscar`, { params })
  }

  getCobradores() {
    return this.http.get<any[]>(`${urlErp}/cobrador/buscar`)
  }
  //ADJUNTOS
  obtenerArchivoBase64(event: Event, maxSizeMB = 10): Promise<ArchivoBase64 | null> {
    return new Promise((resolve, reject) => {
      const input = event.target as HTMLInputElement
      if (!input.files || input.files.length == 0) {
        resolve(null)
        return
      }

      const file = input.files[0]
      if (file.size > maxSizeMB * 1024 * 1024) {
        reject(new Error('FILE_TOO_LARGE'))
        return
      }

      const lector = new FileReader()
      lector.onload = () => {
        resolve({
          nombre: file.name.toLowerCase(),
          formato: file.type,
          base64: lector.result as string
        })
      }
      lector.onerror = () => reject(lector.error)
      lector.readAsDataURL(file)
    })
  }

  //TIPO DE CAMBIO
  getTipoCambio(fecha: string): Observable<TipoCambioResponse> {
    const params = new HttpParams()
      .set('fecha', fecha)
      .set('moneda', 'USD')
      .set('hmoneda', 'PEN')
    return this.http.get<TipoCambioResponse>(`${urlErp}/tcambio/v2/hoy`, { params })
  }


}
